import { useState, useCallback, useEffect, useRef } from "react";
import {
  AlertCircle,
  Save,
  Settings,
  Bell,
  Mail,
  CreditCard,
  Trash2,
} from "lucide-react";
import UserService from "../../services/User";
import SettingsService from "../../services/Settings";
import SubscriptionService from "../../services/Subscription";
import { displayToastMessage } from "../../utils/Toasts";
import config from "../../config";
import { logAndFormatFormError } from "../../utils/Errors";

const initialFormState = {
  accountSettings: {
    email: "",
    currentPassword: "",
  },
  subscription: {
    currentPlan: "basicPlan", // Default plan
  },
};

const SUBSCRIPTION_PLANS = [
  {
    id: "basicPlan",
    name: "Basic Plan",
    price: "$9.99/month",
    features: [
      "10 Strategy Reports per month",
      "Target Audience Profile",
      "Viral Hook Ideas",
      "Trending Hashtags",
      "Basic Content Calendar",
    ],
  },
  {
    id: "viralPlan",
    name: "Viral Plan",
    price: "$19.99/month",
    popular: true,
    features: [
      "20 Strategy Reports per month",
      "Everything in Basic +",
      "Engagement Strategy",
      "Competitor Analysis",
      "Weekly Trend Alerts",
      "Caption Templates",
    ],
  },
  {
    id: "proPlan",
    name: "Pro Plan",
    price: "$49.99/month",
    features: [
      "100 Strategy Reports per month",
      "Everything in Viral +",
      "Cross-Platform Sharing Plan",
      "Partnership Opportunity Finder",
      "Monetization Strategy",
      "Priority Support",
    ],
  },
];

const SettingsForm = ({
  userId,
  userEmail,
  emailNotifications,
  onClose,
  onSuccess,
  onLogout,
}: any) => {
  // Use useRef to store the initial values received from props
  // This helps prevent unnecessary re-renders when props change
  const initialPropsRef = useRef({
    userEmail,
    emailNotifications,
    currentPlan: null,
  } as any);

  // Local form state
  const [formData, setFormData] = useState({
    ...initialFormState,
    accountSettings: {
      ...initialFormState.accountSettings,
      email: userEmail || "",
    },
  });

  // Store email notifications in a separate state variable
  const [emailNotificationsState, setEmailNotificationsState] = useState(
    emailNotifications !== undefined ? emailNotifications : true
  );

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [activeSection, setActiveSection] = useState("accountSettings");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  // Track if subscription data has been loaded
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);

  // Initialize services - moved outside of render to avoid recreating on each render
  const userService = useRef(new UserService()).current;
  const settingsService = useRef(new SettingsService()).current;
  const subscriptionService = useRef(new SubscriptionService()).current;

  // Only fetch subscription once when component mounts
  useEffect(() => {
    const fetchSubscription = async () => {
      if (userId && !subscriptionLoaded) {
        try {
          const resBody = await subscriptionService.getSubscription(userId);
          if (resBody.error) {
            throw new Error(resBody.error);
          }

          const planName = resBody.plan?.name || "basicPlan";

          setFormData((prev: any) => ({
            ...prev,
            subscription: {
              currentPlan: planName,
            },
          }));

          // Store the initial plan for comparison
          initialPropsRef.current.currentPlan = planName;
          setSubscriptionLoaded(true);
        } catch (error: any) {
          const errorMessage = logAndFormatFormError(
            "SettingsForm.fetchSubscription",
            error,
            "Fetching subscription data"
          );
          displayToastMessage("error", errorMessage);
          setSubscriptionLoaded(true); // Mark as loaded even on error to prevent repeated fetches
        }
      }
    };

    fetchSubscription();
  }, [userId, subscriptionService, subscriptionLoaded]);

  // Update email state if prop changes (only if it's a meaningful change)
  useEffect(() => {
    if (
      userEmail !== undefined &&
      userEmail !== initialPropsRef.current.userEmail
    ) {
      setFormData((prev: any) => ({
        ...prev,
        accountSettings: {
          ...prev.accountSettings,
          email: userEmail,
        },
      }));
      initialPropsRef.current.userEmail = userEmail;
    }
  }, [userEmail]);

  // Update email notifications state if prop changes
  useEffect(() => {
    if (
      emailNotifications !== undefined &&
      emailNotifications !== initialPropsRef.current.emailNotifications
    ) {
      setEmailNotificationsState(emailNotifications);
      initialPropsRef.current.emailNotifications = emailNotifications;
    }
  }, [emailNotifications]);

  // Handle text input changes - use memoized callback to prevent recreation on each render
  const handleTextChange = useCallback(
    (section: any, field: any, value: any) => {
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));

      // Clear any errors for this field
      setErrors((prev: any) => {
        if (!prev[field]) return prev;
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    },
    []
  );

  // Handle notification toggle changes
  const handleNotificationToggle = useCallback(() => {
    setEmailNotificationsState((prev: any) => !prev);
  }, []);

  // Handle subscription plan change
  const handlePlanChange = useCallback((planId: any) => {
    setFormData((prev: any) => ({
      ...prev,
      subscription: {
        ...prev.subscription,
        currentPlan: planId,
      },
    }));
  }, []);

  // Validate the form before submission
  const validateForm = useCallback(() => {
    const newErrors: any = {};

    // Only validate email if it's been changed
    if (formData.accountSettings.email !== initialPropsRef.current.userEmail) {
      // Basic email validation
      if (!formData.accountSettings.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.accountSettings.email)) {
        newErrors.email = "Please enter a valid email address";
      }

      // Password is required when changing email
      if (!formData.accountSettings.currentPassword) {
        newErrors.currentPassword = "Password is required to update email";
      }
    }

    return { errors: newErrors, isValid: Object.keys(newErrors).length === 0 };
  }, [
    formData.accountSettings.email,
    formData.accountSettings.currentPassword,
  ]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();

      // Validate form
      const { errors: validationErrors, isValid } = validateForm();
      if (!isValid) {
        setErrors(validationErrors);
        setActiveSection("accountSettings");
        return;
      }

      setSubmitting(true);
      setErrors({});

      try {
        // Track what has been updated for success message
        const updatedItems = [];
        const updatedValues: any = {};

        // Only include account updates if email has changed
        const emailChanged =
          formData.accountSettings.email !== initialPropsRef.current.userEmail;

        // Update email if changed
        if (emailChanged) {
          const updatedAccount = {
            email: formData.accountSettings.email,
          };

          await userService.updateAccount({
            userID: userId,
            email: initialPropsRef.current.userEmail,
            password: formData.accountSettings.currentPassword,
            updatedAccount: updatedAccount,
          });

          updatedItems.push("account information");
          updatedValues.email = formData.accountSettings.email;

          // Update the initial value reference
          initialPropsRef.current.userEmail = formData.accountSettings.email;
        }

        // Check if email notifications changed
        const emailNotificationsChanged =
          emailNotificationsState !==
          initialPropsRef.current.emailNotifications;

        // Update notification settings if changed
        if (emailNotificationsChanged) {
          await settingsService.updateNotifications({
            userId: userId,
            emailNotifications: emailNotificationsState,
          });

          updatedItems.push("notification preferences");
          updatedValues.emailNotifications = emailNotificationsState;

          // Update the initial value reference
          initialPropsRef.current.emailNotifications = emailNotificationsState;
        }

        // Check if subscription plan changed
        const planChanged =
          formData.subscription.currentPlan !==
          initialPropsRef.current.currentPlan;

        // Update subscription plan if changed
        if (planChanged) {
          await subscriptionService.joinOrUpdateSubscription({
            userId: userId,
            subscriptionPlan: formData.subscription.currentPlan,
          });

          updatedItems.push("subscription plan");
          updatedValues.subscriptionPlan = formData.subscription.currentPlan;

          // Update the initial value reference
          initialPropsRef.current.currentPlan =
            formData.subscription.currentPlan;
        }

        // Only show success message if something was updated
        if (updatedItems.length > 0) {
          displayToastMessage(
            "success",
            `Successfully updated ${updatedItems.join(", ")}`
          );

          // Clear current password field
          setFormData((prev: any) => ({
            ...prev,
            accountSettings: {
              ...prev.accountSettings,
              currentPassword: "",
            },
          }));

          // Call success callback with only the changed values
          if (onSuccess && Object.keys(updatedValues).length > 0) {
            onSuccess(updatedValues);
          }
        } else {
          displayToastMessage("info", "No changes were made");
        }
      } catch (error: any) {
        setErrors({
          form: error.message || "An error occurred while updating settings",
        });
        const errorMessage = logAndFormatFormError(
          "SettingsForm.handleSubmit",
          error,
          "Updating settings"
        );
        displayToastMessage("error", errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
    [
      formData,
      emailNotificationsState,
      userId,
      validateForm,
      onSuccess,
      userService,
      settingsService,
      subscriptionService,
    ]
  );

  // Handle account deletion request
  const handleDeleteAccount = useCallback(async () => {
    if (deleteConfirmation !== "DELETE") {
      setErrors({
        deleteAccount: "Please type DELETE to confirm account deletion",
      });
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      await settingsService.requestAccountDeletion({
        userId: userId,
        confirmDeletion: true,
      });

      displayToastMessage(
        "success",
        "Account deletion request submitted. You will receive further instructions via email."
      );

      if (onLogout) {
        await onLogout();
      }

      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      setErrors({
        deleteAccount: error.message || "Failed to process deletion request",
      });
      const errorMessage = logAndFormatFormError(
        "SettingsForm.handleDeleteAccount",
        error,
        "Requesting account deletion"
      );
      displayToastMessage("error", errorMessage);
    } finally {
      setSubmitting(false);
    }
  }, [deleteConfirmation, userId, settingsService, onLogout, onClose]);

  // Memoize the section click handler
  const handleSectionClick = useCallback((sectionId: any) => {
    setActiveSection(sectionId);
  }, []);

  // Memoize the delete confirmation handler
  const handleDeleteConfirmationChange = useCallback((value: any) => {
    setDeleteConfirmation(value);
  }, []);

  // UI Components - moved outside component to prevent recreation on each render
  const Section = useCallback(
    ({ id, title, children, icon }: any) => (
      <div
        className={`mb-6 bg-white rounded-xl p-4 md:p-6 shadow-md ${
          activeSection === id ? "border-2 border-indigo-400" : ""
        }`}
        onClick={() => handleSectionClick(id)}
      >
        <div className="flex items-center mb-4 text-indigo-600">
          {icon}
          <h2 className="text-xl font-bold ml-2">{title}</h2>
        </div>
        {activeSection === id && children}
      </div>
    ),
    [activeSection, handleSectionClick]
  );

  const InputField = useCallback(
    ({
      section,
      field,
      label,
      value,
      onChange,
      placeholder,
      type = "text",
      error,
    }: any) => (
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 text-gray-700">
          {label}
        </label>
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(section, field, e.target.value)}
          placeholder={placeholder}
          className={`w-full px-4 py-2 rounded-lg border ${
            error ? "border-red-500 bg-red-50" : "border-gray-300"
          } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition`}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {error}
          </p>
        )}
      </div>
    ),
    []
  );

  const ToggleField = useCallback(
    ({ label, field, enabled, onChange }: any) => (
      <div className="flex items-center justify-between py-3 border-b border-gray-200">
        <span className="text-gray-700">{label}</span>
        <button
          type="button"
          onClick={() => onChange(field)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            enabled ? "bg-indigo-600" : "bg-gray-200"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    ),
    []
  );

  const SubscriptionCard = useCallback(
    ({ plan, isActive, onSelect, index }: any) => (
      <div
        className={`border rounded-lg p-4 mb-4 cursor-pointer transition-all relative ${
          isActive
            ? "border-indigo-500 bg-indigo-50 shadow-md"
            : "border-gray-200 hover:border-indigo-300"
        }`}
        onClick={() => onSelect(plan.id)}
      >
        {plan.popular && (
          <div className="absolute -top-3 -right-3 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full">
            MOST POPULAR
          </div>
        )}
        <div className="text-center mb-3">
          <h3 className="font-bold text-lg text-gray-800">
            {plan.name.replace(" Plan", "")}
          </h3>
          <div className="mt-1">
            <span className="font-bold text-xl text-indigo-600">
              {plan.price}
            </span>
          </div>
        </div>
        <ul className="text-sm text-gray-600 mb-4">
          {plan.features.map((feature: any, featureIndex: number) => (
            <li key={featureIndex} className="mb-2 flex items-start">
              <span className="text-green-500 mr-2 mt-0.5">✓</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <div className="mt-2 text-center">
          {isActive ? (
            <div className="bg-indigo-100 text-indigo-700 px-3 py-2 rounded font-medium">
              Current Plan
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(plan.id);
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded font-medium transition-colors"
            >
              Select Plan
            </button>
          )}
        </div>
        {index === SUBSCRIPTION_PLANS.length - 1 && (
          <div className="text-center text-xs text-gray-500 mt-4">
            Cancel anytime – no long-term commitments.
          </div>
        )}
      </div>
    ),
    []
  );

  // Check if any changes have been made to determine if submit button should be enabled
  const hasChanges =
    formData.accountSettings.email !== initialPropsRef.current.userEmail ||
    emailNotificationsState !== initialPropsRef.current.emailNotifications ||
    (subscriptionLoaded &&
      formData.subscription.currentPlan !==
        initialPropsRef.current.currentPlan);

  return (
    <div className="mx-auto px-4 py-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
      {/* Header Section */}
      <div className="text-center mb-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-3">
          Account Settings
        </h1>
        <p className="text-gray-700 max-w-md mx-auto mb-4">
          Manage your account information, notification preferences, and
          subscription
        </p>
      </div>

      {/* Form Error */}
      {errors.form && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span>{errors.form}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Section Navigation for Mobile */}
        <div className="flex overflow-x-auto pb-2 mb-4 md:hidden space-x-2">
          <button
            type="button"
            onClick={() => handleSectionClick("accountSettings")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "accountSettings"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Account
          </button>
          <button
            type="button"
            onClick={() => handleSectionClick("notificationSettings")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "notificationSettings"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Notifications
          </button>
          <button
            type="button"
            onClick={() => handleSectionClick("subscriptionSettings")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "subscriptionSettings"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700"
            }`}
          >
            Subscription
          </button>
          <button
            type="button"
            onClick={() => handleSectionClick("dangerZone")}
            className={`px-4 py-2 rounded-full flex-shrink-0 text-sm font-medium ${
              activeSection === "dangerZone"
                ? "bg-red-600 text-white"
                : "bg-white text-red-600"
            }`}
          >
            Delete Account
          </button>
        </div>

        <Section
          id="accountSettings"
          title="Account Information"
          icon={<Settings className="h-5 w-5" />}
        >
          <InputField
            section="accountSettings"
            field="email"
            label="Email Address"
            value={formData.accountSettings.email}
            onChange={handleTextChange}
            placeholder="your@email.com"
            error={errors.email}
          />

          {formData.accountSettings.email !==
            initialPropsRef.current.userEmail && (
            <InputField
              section="accountSettings"
              field="currentPassword"
              label="Current Password"
              value={formData.accountSettings.currentPassword}
              onChange={handleTextChange}
              placeholder="Enter your current password to confirm changes"
              type="password"
              error={errors.currentPassword}
            />
          )}
        </Section>

        <Section
          id="notificationSettings"
          title="Notification Preferences"
          icon={<Bell className="h-5 w-5" />}
        >
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-4">
              Choose which notifications you'd like to receive
            </p>

            <ToggleField
              label="Email Notifications"
              field="emailNotifications"
              enabled={emailNotificationsState}
              onChange={handleNotificationToggle}
            />
          </div>
        </Section>

        <Section
          id="subscriptionSettings"
          title="Subscription Plan"
          icon={<CreditCard className="h-5 w-5" />}
        >
          {!subscriptionLoaded ? (
            <div className="flex justify-center items-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-indigo-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="ml-2 text-indigo-600">
                Loading subscription data...
              </span>
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Choose the plan that works best for you
                </p>
                {formData.subscription.currentPlan && (
                  <a
                    href={config.links.stripeCustomerPortalURL}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Manage Billing
                  </a>
                )}
              </div>

              <h3 className="text-xl font-bold text-center mb-2">
                Simple Pricing for Creators
              </h3>
              <p className="text-center text-gray-600 mb-6">
                Choose the plan that matches your growth goals
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {SUBSCRIPTION_PLANS.map((plan, index) => (
                  <SubscriptionCard
                    key={plan.id}
                    plan={plan}
                    isActive={formData.subscription.currentPlan === plan.id}
                    onSelect={handlePlanChange}
                    index={index}
                  />
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-4">
                Cancel anytime – no long-term commitments.
              </p>

              {/* Additional Billing Portal Link at the Bottom */}
              {formData.subscription.currentPlan && (
                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    Need to update your payment method or billing information?
                  </p>
                  <a
                    href={config.links.stripeCustomerPortalURL}
                    className="inline-flex items-center px-5 py-2.5 font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Go to Billing Portal
                  </a>
                </div>
              )}
            </div>
          )}
        </Section>

        <Section
          id="dangerZone"
          title="Delete Account"
          icon={<Trash2 className="h-5 w-5 text-red-600" />}
        >
          <div className="mb-4 bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="text-lg font-medium text-red-700 mb-2">
              Delete Your Account
            </h3>
            <p className="text-sm text-red-600 mb-4">
              Warning: This action is permanent and cannot be undone. All your
              data will be permanently deleted.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">
                To confirm, type "DELETE" below
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => handleDeleteConfirmationChange(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full px-4 py-2 rounded-lg border border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              {errors.deleteAccount && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.deleteAccount}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== "DELETE" || submitting}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Processing..." : "Delete Account Permanently"}
            </button>
          </div>
        </Section>

        {/* Information Box */}
        <div className="bg-white rounded-xl p-4 md:p-6 mb-6 shadow-md">
          <div className="flex items-start">
            <Mail className="h-5 w-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-gray-900">Account Information</h3>
              <p className="text-sm text-gray-500 mt-1">
                We'll always send you important account notifications such as
                security alerts and receipts, regardless of your notification
                settings.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={
              submitting || activeSection === "dangerZone" || !hasChanges
            }
            className={`px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-200 ${
              !hasChanges || submitting || activeSection === "dangerZone"
                ? "opacity-50 cursor-not-allowed"
                : "transform hover:-translate-y-1"
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Save className="mr-2 h-5 w-5" />
                Save Settings
              </span>
            )}
          </button>
        </div>

        {/* Cancel link */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsForm;
